import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography, withStyles } from '@mui/material';
import { ArrowBackIosNewRounded, ArrowForwardIosRounded, ArrowBack } from '@mui/icons-material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Edit } from '../constructor/work/Edit';
import { EventSelectInput, SelectInputType } from '../helpers/events';
import * as THREE from 'three';
import { DivAnimate } from './DivAnimate';

interface Props {
  label?: string;
  text: string;
  nameA: string;
  nameB: string;
  angleA: number;
  angleB: number;
}
export const InputSizeSide: FC<Props> = ({ text, label, nameA, nameB, angleA, angleB }) => {
  const [currentText, setCurrentText] = useState('');
  const inputRef = useRef<HTMLInputElement>();
  const isActiveInput = useRef(true);

  const handlerChangeSideLength = (sym: string, direct: 'forward' | 'back') => {
    isActiveInput.current = true;
    Edit.changeSideLength(sym, +currentText / 1000, direct);

    if (inputRef.current) {
      inputRef.current?.blur();
    }
  };

  const handlerListenerSelectText = (evt: CustomEvent<SelectInputType>) => {
    const text = nameA + nameB;
    if (text === evt.detail.nameSide) {
      if (inputRef.current) {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
  };

  const changeText = (evt: ChangeEvent<HTMLInputElement>) => {
    isActiveInput.current = false;
    const text = evt.target.value.replace(/[^0-9]/g, '');
    setCurrentText(text);
  };

  const getAngle = (angle: number) => {
    let _angle = angle;
    if (+currentText < +text) {
      _angle -= Math.PI;
    }
    return THREE.MathUtils.radToDeg(_angle);
  };

  const handlerBlurInput = () => {
    setTimeout(() => {
      if (currentText !== text && !isActiveInput.current) {
        setCurrentText(text);
      }
    }, 300);
  };

  useEffect(() => {
    setCurrentText(text);
  }, [text]);

  useEffect(() => {
    document.addEventListener(EventSelectInput, handlerListenerSelectText);
    return () => {
      document.addEventListener(EventSelectInput, handlerListenerSelectText);
    };
  }, []);

  return (
    <Box mt={1}>
      <Typography color="#fff" component="center">
        {`${label}, mm`}
      </Typography>
      <Box color="#fff" alignItems="center" width="100%" display="flex">
        <Typography sx={{ marginRight: '5px' }} color="#fff" component="center">
          {nameA}
        </Typography>
        {currentText !== text ? (
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{
              p: 0.5,
              padding: '0',
            }}
            onClick={() => handlerChangeSideLength(nameA, 'forward')}
          >
            <DivAnimate>
              <ArrowBack
                sx={{
                  transform: `rotateZ(${getAngle(-angleA)}deg)`,
                  color: '#00ff00',
                }}
              />
            </DivAnimate>
          </IconButton>
        ) : (
          <FiberManualRecordIcon />
        )}
        <TextField
          autoComplete="off"
          inputRef={inputRef}
          value={currentText}
          onChange={changeText}
          variant="outlined"
          onBlur={handlerBlurInput}
          size="small"
          sx={{
            '& input': {
              color: '#000',
              borderColor: '#ffffff',
              borderRadius: '4px',
              backgroundColor: '#fff',
              fontSize: '12px',
              padding: '5px',
            },
          }}
        />

        {currentText !== text ? (
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{
              p: 0.5,
              padding: '0',
            }}
            onClick={() => handlerChangeSideLength(nameB, 'back')}
          >
            <DivAnimate>
              <ArrowBack sx={{ transform: `rotateZ(${getAngle(-angleB)}deg)`, color: '#00ff00' }} />
            </DivAnimate>
          </IconButton>
        ) : (
          <FiberManualRecordIcon />
        )}
        <Typography sx={{ ml: '5px' }} color="#fff" component="center">
          {nameB}
        </Typography>
      </Box>
    </Box>
  );
};
